// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/FAQ.js

import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiHelpCircle } from 'react-icons/fi';
import useBrandColors from '../utils/useBrandColors';

const FAQItem = ({ item, isOpen, toggle, index }) => {
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  return (
    <div className="border-b last:border-0 transition-all duration-300" style={{ borderColor: `${primary}15` }}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between py-4 text-left transition-colors duration-300 group"
        style={{ fontFamily: fontBody }}
      >
        <div className="flex items-start gap-3 pr-8">
          <div 
            className="flex-shrink-0 mt-1 transition-colors duration-300"
            style={{ color: isOpen ? secondary : primary }}
          >
            <FiHelpCircle className="w-5 h-5" />
          </div>
          <span 
            className="font-medium transition-colors duration-300"
            style={{ 
              color: isOpen ? primary : '#1F2937',
              fontFamily: fontHeading,
            }}
          >
            {item.question}
          </span>
        </div>
        <span 
          className="flex-shrink-0 ml-4 p-1 rounded-full transition-all duration-300"
          style={{ 
            backgroundColor: isOpen ? `${primary}10` : 'transparent',
            color: isOpen ? primary : '#9CA3AF',
          }}
        >
          {isOpen ? (
            <FiChevronUp className="w-5 h-5" />
          ) : (
            <FiChevronDown className="w-5 h-5" />
          )}
        </span>
      </button>
      
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 pb-4' : 'max-h-0'
        }`}
        style={{ fontFamily: fontBody }}
      >
        <p className="pl-10 leading-relaxed" style={{ color: '#6B7280' }}>
          {item.answer}
        </p>
        {item.category && (
          <span 
            className="inline-block mt-2 ml-10 px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${secondary}15`,
              color: secondary,
            }}
          >
            {item.category}
          </span>
        )}
      </div>
    </div>
  );
};

const FAQCategoryCard = ({ category, items }) => {
  const { primary,  fontBody, fontHeading } = useBrandColors();

  return (
    <div 
      className="bg-white rounded-2xl border p-6 transition-all duration-300 hover:shadow-lg"
      style={{ 
        borderColor: `${primary}15`,
        fontFamily: fontBody,
      }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div 
          className="p-2 rounded-xl"
          style={{ backgroundColor: `${primary}10` }}
        >
          <FiHelpCircle className="w-5 h-5" style={{ color: primary }} />
        </div>
        <h3 
          className="font-bold text-lg"
          style={{ 
            color: primary,
            fontFamily: fontHeading,
          }}
        >
          {category}
        </h3>
        <span 
          className="ml-auto text-xs px-2 py-0.5 rounded-full"
          style={{ 
            backgroundColor: `${primary}10`,
            color: primary,
          }}
        >
          {items.length}
        </span>
      </div>
      
      <div className="space-y-4">
        {items.map((item, idx) => (
          <div key={item._id || idx} className="pb-3 border-b last:border-0 last:pb-0" style={{ borderColor: `${primary}08` }}>
            <h4 
              className="font-medium text-sm mb-1"
              style={{ 
                color: '#374151',
                fontFamily: fontHeading,
              }}
            >
              {item.question}
            </h4>
            <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>
              {item.answer}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const FAQ = ({ faq }) => {
  const { sectionTitle, sectionSubtitle, displayStyle = 'accordion', items = [] } = faq || {};
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();
  const [openIndex, setOpenIndex] = useState(null);

  if (!items || items.length === 0) return null;

  const toggleItem = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const groupedItems = displayStyle === 'grid' || displayStyle === 'cards'
    ? items.reduce((acc, item) => {
        const category = item.category || 'General';
        if (!acc[category]) acc[category] = [];
        acc[category].push(item);
        return acc;
      }, {})
    : null;

  return (
    <section id="faq" className="py-16 md:py-24 bg-gray-50/30">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {(sectionTitle || sectionSubtitle) && (
            <div className="text-center mb-12 md:mb-16">
              <span 
                className="inline-block text-xs font-light tracking-[0.2em] uppercase mb-3"
                style={{ color: secondary }}
              >
                FAQ
              </span>
              {sectionTitle && (
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                  style={{ 
                    color: primary,
                    fontFamily: fontHeading,
                  }}
                >
                  {sectionTitle}
                </h2>
              )}
              {sectionSubtitle && (
                <p 
                  className="text-lg font-light max-w-2xl mx-auto"
                  style={{ 
                    color: '#6B7280',
                    fontFamily: fontBody,
                  }}
                >
                  {sectionSubtitle}
                </p>
              )}
              <div className="mt-6 flex justify-center">
                <div 
                  className="h-1 w-20 rounded-full" 
                  style={{ 
                    background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                  }} 
                />
              </div>
            </div>
          )}

          {displayStyle === 'accordion' ? (
            <div 
              className="bg-white rounded-2xl shadow-sm border px-4 md:px-6"
              style={{ borderColor: `${primary}15` }}
            >
              {items.map((item, index) => (
                <FAQItem
                  key={item._id || index}
                  item={item}
                  isOpen={openIndex === index}
                  toggle={() => toggleItem(index)}
                  index={index}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(groupedItems || {}).map(([category, categoryItems]) => (
                <FAQCategoryCard 
                  key={category} 
                  category={category} 
                  items={categoryItems}
                />
              ))}
            </div>
          )}

          {/* Still Have Questions */}
          <div 
            className="mt-12 text-center p-8 rounded-2xl border"
            style={{ 
              borderColor: `${primary}15`,
              backgroundColor: `${primary}05`,
              fontFamily: fontBody,
            }}
          >
            <p 
              className="font-medium mb-2"
              style={{ 
                color: primary,
                fontFamily: fontHeading,
              }}
            >
              Still have questions?
            </p>
            <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
              Can't find the answer you're looking for? Please contact our team.
            </p>
            <button
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:scale-105 active:scale-95"
              style={{ 
                backgroundColor: primary,
                color: '#ffffff',
                fontFamily: fontBody,
              }}
            >
              <span>Contact Support</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;